// Plain-date helpers for the Tasks list groups and calendar. Weeks start on
// Monday; "today" is PEXIFY's operating day (Eastern), like every other screen.
import { addPlainDays, type PlainDate } from '../../lib/datetime'
import type { StaffTask } from './api'

export type DueBucket = 'overdue' | 'today' | 'thisWeek' | 'nextWeek' | 'later' | 'noDate' | 'done'

export const DUE_BUCKETS: DueBucket[] = [
  'overdue',
  'today',
  'thisWeek',
  'nextWeek',
  'later',
  'noDate',
  'done',
]

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(day: PlainDate): number {
  const [y, m, d] = day.split('-').map(Number)
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
}

export function startOfWeek(day: PlainDate): PlainDate {
  return addPlainDays(day, -weekdayIndex(day))
}

export function dueBucket(task: StaffTask, today: PlainDate): DueBucket {
  if (task.status === 'done') return 'done'
  const due = task.due_date as PlainDate | null
  if (!due) return 'noDate'
  if (due < today) return 'overdue'
  if (due === today) return 'today'
  const nextWeek = addPlainDays(startOfWeek(today), 7)
  if (due < nextWeek) return 'thisWeek'
  if (due < addPlainDays(nextWeek, 7)) return 'nextWeek'
  return 'later'
}

export function groupByDue(tasks: StaffTask[], today: PlainDate): Record<DueBucket, StaffTask[]> {
  const groups = Object.fromEntries(DUE_BUCKETS.map((b) => [b, [] as StaffTask[]])) as Record<
    DueBucket,
    StaffTask[]
  >
  for (const task of tasks) groups[dueBucket(task, today)].push(task)
  return groups
}

/** The 6×7 (Monday-first) grid of days that shows `month` (YYYY-MM). */
export function monthGrid(month: string): PlainDate[] {
  const first = `${month}-01` as PlainDate
  const start = startOfWeek(first)
  return Array.from({ length: 42 }, (_, i) => addPlainDays(start, i))
}

export function weekGrid(day: PlainDate): PlainDate[] {
  const start = startOfWeek(day)
  return Array.from({ length: 7 }, (_, i) => addPlainDays(start, i))
}

export function shiftMonth(month: string, amount: number): string {
  const [y, m] = month.split('-').map(Number)
  const index = y * 12 + (m - 1) + amount
  return `${String(Math.floor(index / 12)).padStart(4, '0')}-${String((index % 12) + 1).padStart(2, '0')}`
}
